/**
 * The governed semantic layer, and the report-definition wire shapes.
 *
 * ## Why this is here and not in `apps/api/src/demo/overlay.ts`
 *
 * It used to live there — `SEMANTIC_LAYER`, a bare list of nine dimension labels
 * and six metric labels, in the module whose own header says *"nothing in this
 * file is written to the database"*. The vocabulary that is supposed to survive a
 * schema change was defined in the demo file, beside the invented vendor ratings.
 * It now lives in the shared contract, next to the permission matrix, for the same
 * reason that does: the API enforces it, the web app renders it, and there must be
 * exactly one of it.
 *
 * ## What a field IS
 *
 * A **label plus a per-view column mapping**. Deliberately not a raw column name:
 * the point of a semantic layer is that a saved report survives a rename, and
 * exposing `cost_entry.amount` to a builder guarantees it will not. A report
 * author picks `Downtime hours`; the server resolves that to
 * `v_asset_downtime_by_period.downtime_hours` at run time.
 *
 * A field is available on a view only if this file says so. That is a **security
 * boundary, not a convenience**: `v_work_order_search` carries `search_text` and
 * `search_vector`, which fold **every comment body including `internal_only`** into
 * one unfiltered blob (B2 — the same reason `searchPredicate` offers it only to a
 * caller holding `seeInternalComments`).
 * A report runner that did `SELECT *` from a gold view would publish those to
 * anyone who can build a report. Nothing here maps to either column, and the
 * runner may only ever select what a field names.
 *
 * ## Three entries in the old list had no backing column. TWO ARE NOW FIXED.
 *
 * Kept as the trail rather than deleted, because the shape of each gap is what
 * explains the builder's refusals — and because a header that quietly stops
 * describing the file is how a caveat comes to be disbelieved.
 *
 * - **Terminal** — ~~mapped, but only on `v_asset_master`
 *   (`current_location_name`). No cost, downtime or work-order gold view carries
 *   a location column, so *spend by terminal* cannot be produced today.~~
 *   **FIXED 9 September 2026 by migration `0222` (TRBUD-218)**, which put
 *   `terminal_name` on the five metric-carrying views and re-pointed the mapping
 *   at `domicile_location_id` — home base, the column that has a writer.
 *   `current_location_id` has none at all, so the old mapping was to a column
 *   NULL on every row of the fleet. `unmappedNote` now states the meaning and the
 *   cold-start reading rather than denying the capability.
 * - **Category** — ~~`work_order_category` and `work_order_category_link` exist
 *   as tables, but **no gold view exposes them**, so the dimension has no backing
 *   at all.~~ **FIXED by migration `0172` (TRBUD-154 slice 1)**,
 *   `v_work_order_category_spend`. Its note carries the over-count warning
 *   instead, and `0222` gave that view asset and terminal too.
 * - **Cost — vendor vs in-house** — not a measure, a *split of* one. **Still
 *   true, and it is not a gap to close**: it resolved to two columns on
 *   `v_departmental_spend`, never to one, so it is carried here as two mapped
 *   metrics (`cost_vendor`, `cost_in_house`) that a report puts in the metric and
 *   compare wells. `0222` gave that view `unit_number` and `terminal_name`, so
 *   the split can finally be grouped by something other than a work-order number.
 */

import { z } from "zod";
import { strictObject } from "./requests.js";
// TRBUD-222 / DEC-03 — the two downtime numbers are named in one place.
import { ASSET_DOWNTIME_LABEL } from "./downtime-labels.js";

/**
 * The gold views a report may read. Nothing outside this list is addressable.
 *
 * **This array's ORDER is load-bearing and is not a design.** `resolveReportPlan`
 * takes the first entry carrying every selected field, so an alphabetical accident
 * decides any selection two views can answer.
 *
 * ## The eight combinations that were wrong, and how they were fixed (TRBUD-218)
 *
 * This paragraph used to END here, reporting the defect and ruling out both
 * available fixes. Kept as the statement of the problem, because the problem was
 * real and the measurement was right:
 *
 * > Ten metric-by-dimension combinations are settled that way, and four of them
 * > are already wrong: `work_orders` with no dimension, by `asset`, by `vendor`
 * > and by `completed_date` all land on `v_awaiting_final_cost`, which is FILTERED
 * > to work orders closed and still owed a cost — so "how many work orders by
 * > vendor" quietly answers a narrower question. Adding `v_work_order_queue` moved
 * > four more, for the same reason: `work_orders` by `stage`, `priority`,
 * > `work_order_type` and `created_date` resolve onto the queue view rather than
 * > `v_work_order_search`, because it sorts earlier.
 *
 * It then ruled out demoting filtered views, "that breaks `awaiting_final_cost`,
 * whose selection is exactly `work_orders` by `vendor`". **That objection is
 * answered by `preferredView`, which the same paragraph already relied on one
 * sentence earlier.** A stored definition passes its own `source_view` as
 * `preferredView` and keeps it, and both built-ins whose meaning lives in their
 * view's `WHERE` — `awaiting_final_cost` and `unassigned_aged` — are stored
 * definitions. Nothing reaches the plain array-position tiebreak except a report
 * being built for the first time, which by definition has no meaning to preserve.
 *
 * So `REPORT_VIEW_IS_NARROWED` below marks the two views whose `WHERE` restricts
 * the POPULATION rather than deriving the measure, and `resolveReportPlan` prefers
 * an unnarrowed candidate over a narrowed one. It is a preference and never an
 * exclusion: `queued_days` and `queued_hours` exist only on the queue view, so
 * they still resolve to it.
 *
 * The remaining two of the ten are `cost_total` and `work_orders` landing on
 * unnarrowed views by array position. Those are arbitrary rather than wrong, and
 * reordering to prefer one is still not a fix — it only moves which arbitrary
 * choice is made.
 */
export const REPORT_SOURCE_VIEWS = [
  "v_asset_cost_history",
  "v_asset_downtime_by_period",
  // TRBUD-336. The day-grain view the Reports downtime chart reads, added so the
  // "Downtime by unit" built-in can count exactly the last 30 days. Deliberately
  // AFTER the month view rather than at its alphabetical place: it carries no
  // field the month view lacks, so placed here it wins no selection, and only a
  // definition stored against it (the built-in, through `preferredView`) reads
  // it. Placed first, it would move every new downtime report onto it.
  "v_asset_downtime_by_day",
  "v_asset_master",
  "v_awaiting_final_cost",
  "v_departmental_spend",
  "v_labour_roi",
  "v_part_inventory_position",
  // Migration 0173, TRBUD-141. Inserted at its ALPHABETICAL position, which is
  // not cosmetic given the paragraph above: the only field it shares with an
  // earlier view is a DIMENSION (`vendor`, `work_order`, `part`), and its one
  // metric — `variance_amount` — is carried by no other view at all. So every
  // selection that resolved somewhere yesterday still resolves there today, and
  // the only combinations this entry can win are ones that previously refused.
  "v_purchase_order_invoice_variance",
  "v_vendor_outstanding_balance",
  "v_vendor_spend_ranking",
  // Migration 0172, TRBUD-154. Also at its alphabetical position, and checked
  // the same way: it sorts AFTER `v_awaiting_final_cost` (3) and
  // `v_departmental_spend` (4), which are the views that currently win
  // `work_orders` and `cost_total`, so it cannot take a combination off either.
  // `created_date` is deliberately NOT mapped onto it for exactly this reason —
  // `work_orders` by `created_date` resolves to `v_work_order_queue`, which
  // sorts after this entry, so mapping it here WOULD have moved that report.
  "v_work_order_category_spend",
  "v_work_order_cohort",
  "v_work_order_cost_total",
  "v_work_order_queue",
  "v_work_order_search",
] as const;

export type ReportSourceView = (typeof REPORT_SOURCE_VIEWS)[number];

/**
 * The column a timeframe filter applies to on each view, or `null` for a view
 * that has no period at all.
 *
 * `v_departmental_spend` is the `null` that matters and it is not an oversight:
 * it is **one row per work order for all time**, which is why `read/insights.ts`
 * already warns that its spend split and the spend headline are measured over
 * different things. A report on it therefore covers everything, and the run says
 * so in its caveat rather than quietly returning a figure the reader will assume
 * is windowed.
 */
export const REPORT_PERIOD_COLUMN: Record<ReportSourceView, string | null> = {
  v_asset_cost_history: "period_start",
  v_asset_downtime_by_period: "period_start",
  // A fleet-zone DAY, so a window starts on its own day and nothing is floored.
  v_asset_downtime_by_day: "day",
  v_asset_master: null,
  /**
   * `null`, and this is the second `null` that matters.
   *
   * It was `completed_at`, and that silently emptied the report. The view's only
   * predicate is `wo.cost_status = 'awaiting_final_cost'` — it places **no
   * completion requirement** on the rows it admits, and its own
   * `waiting_duration` is `now() - COALESCE(closed_at, completed_at,
   * created_at)`, a three-way coalesce that exists precisely because
   * `completed_at` is often NULL there. `PATCH /work-orders/:id/cost-status` is
   * a bare UPDATE with no stage check, so the state is fully reachable through
   * the normal route.
   *
   * A `WHERE completed_at >= <start>` therefore drops exactly the work the
   * report exists to surface. Measured: the Overview tile said 1 while the
   * report over the same view said 0 rows — two surfaces disagreeing about one
   * fact, and the disagreement hiding an unbilled vendor invoice.
   *
   * Reported rather than filtered: with no period column the run now carries the
   * "timeframe is NOT applied, this covers all time" caveat, which is a true
   * statement the reader can act on. The alternative — a `period_at` expression
   * on the view — is a schema change that would still have to pick between
   * closed, completed and created, which is the choice `waiting_duration`
   * already had to make.
   */
  v_awaiting_final_cost: null,
  v_departmental_spend: null,
  v_labour_roi: "period_start",
  /**
   * `null`: on hand, reserved and available are BALANCES, as of now. TRBUD-336,
   * finding 30.
   *
   * This was `last_movement_at`, so a timeframe kept only parts that MOVED in
   * the window: a part sitting untouched on the shelf vanished from "Parts on
   * hand", and the 57 MaintainX opening balances vanished from every 30-day
   * report. A balance has no window. The run says so instead
   * (`REPORT_VIEW_IS_BALANCE`), and the metric's label says "as of now".
   */
  v_part_inventory_position: null,
  /**
   * `posted_at` — when the money reached the ledger, not when the order was
   * raised.
   *
   * A variance is a fact about an INVOICE, and the invoice is what `posted_at`
   * dates. `purchase_order.created_at` would window the report by when somebody
   * decided to buy something, which is the wrong end: an order raised in March
   * and billed in September is a September finding, and the person chasing it is
   * looking at September. A real timestamp, so no month-bucket flooring applies.
   */
  v_purchase_order_invoice_variance: "posted_at",
  v_vendor_outstanding_balance: "invoice_date",
  v_vendor_spend_ranking: "period_start",
  /**
   * `created_at` — the work order's, and the view says so rather than implying
   * a spend window.
   *
   * This one has a genuine choice and neither answer is free. Windowing on
   * `last_posted_at` would ask "what was BILLED in the window", which silently
   * drops every categorised work order that has not been costed yet — including
   * all of them on a database where nothing has been billed, which is the
   * cold-start state. Windowing on creation asks "of the work raised in the
   * window, how much has it cost so far", which is answerable from day one and
   * degrades honestly: a young work order simply carries a small number.
   *
   * A real timestamp, not a month bucket, so TRBUD-85's flooring does not apply.
   */
  v_work_order_category_spend: "created_at",
  v_work_order_cohort: "cohort_month",
  v_work_order_cost_total: "last_posted_at",
  v_work_order_queue: "created_at",
  v_work_order_search: "created_at",
};

/**
 * Which period columns are CALENDAR-MONTH BUCKETS rather than raw timestamps.
 *
 * This is TRBUD-85, in the one place the fix had never reached. A bucketed
 * `period_start` is always the 1st, so a day-granular threshold
 * (`period_start >= today - 30`) always lands INSIDE the previous month and
 * discards that whole bucket — where most of the hours or the spend usually are.
 * The column says "30d" and computes month-to-date, wrong in the reassuring
 * direction. `v_asset_downtime_by_period` carries a COMMENT ON VIEW forbidding
 * exactly that.
 *
 * The dashboard was fixed by flooring its window to the month
 * (`monthBucketStart`); the report BUILDER was not, and measured 10.00 h against
 * a true 14.00 h for the same asset over the same nominal window. Declaring the
 * grain here is what lets `runReportDefinition` floor the start for these views
 * and only these views — `quarter` and `ytd` already start on the 1st, so `30d`
 * is the only unsafe timeframe.
 *
 * Measured by `to_char(period_start, 'DD') = '01'` against the live views, which
 * is why `v_work_order_cohort` is on the list under a differently named column.
 * A view with no period column is trivially `false`.
 */
export const REPORT_PERIOD_IS_MONTH_BUCKET: Record<ReportSourceView, boolean> = {
  v_asset_cost_history: true,
  v_asset_downtime_by_period: true,
  v_asset_downtime_by_day: false,
  v_asset_master: false,
  v_awaiting_final_cost: false,
  v_departmental_spend: false,
  v_labour_roi: true,
  v_part_inventory_position: false,
  // `posted_at` and `created_at` are raw timestamptz columns, so a 30-day window
  // is a 30-day window and nothing needs flooring.
  v_purchase_order_invoice_variance: false,
  v_vendor_outstanding_balance: false,
  // TRUE, and true at last. TRBUD-336, finding 1: the view bucketed by YEAR
  // until migration 20261008163126, while this said month. Flooring a window to
  // the 1st of a month then dropped the only bucket (1 January), so "Last 30
  // days" and "This quarter" returned nothing. The view now buckets by month.
  v_vendor_spend_ranking: true,
  v_work_order_category_spend: false,
  v_work_order_cohort: true,
  v_work_order_cost_total: false,
  v_work_order_queue: false,
  v_work_order_search: false,
};

/**
 * Which period columns are INSTANTS (`timestamptz`) rather than dates.
 * TRBUD-336, the rest of finding 21.
 *
 * A window start is a fleet-zone DATE. Compared with a `timestamptz`, a bare
 * date is read at midnight in the SESSION's zone, and the API's sessions run in
 * UTC, so "from 8 September" began at 7 pm Central on 7 September. A window over
 * an instant therefore starts at fleet-zone midnight, as the summary cards do; a
 * date column compares with the date as it is. A dbtest holds this list to the
 * live column types, so it cannot drift from them.
 */
export const REPORT_PERIOD_IS_INSTANT: Record<ReportSourceView, boolean> = {
  v_asset_cost_history: false,
  v_asset_downtime_by_period: false,
  v_asset_downtime_by_day: false,
  v_asset_master: false,
  v_awaiting_final_cost: false,
  v_departmental_spend: false,
  v_labour_roi: false,
  v_part_inventory_position: false,
  v_purchase_order_invoice_variance: true,
  v_vendor_outstanding_balance: false,
  v_vendor_spend_ranking: false,
  v_work_order_category_spend: true,
  v_work_order_cohort: false,
  v_work_order_cost_total: true,
  v_work_order_queue: true,
  v_work_order_search: true,
};

/**
 * The database's own CHECK, restated so the API can refuse before it writes.
 *
 * Anchored at both ends and with no quantified alternation, so it cannot
 * backtrack — the same discipline `packages/monitoring-db/src/guards.ts` keeps for
 * its ReDoS findings.
 */
const SOURCE_VIEW_PATTERN = /^v_[a-z_0-9]+$/;

/**
 * The views whose `WHERE` narrows the POPULATION rather than deriving the measure.
 *
 * `resolveReportPlan` prefers an unnarrowed candidate over one of these when the
 * caller has no stored view to honour. TRBUD-218 — see the long note on
 * `REPORT_SOURCE_VIEWS` for the eight combinations this fixes.
 *
 * **Exactly two entries, and the test of membership is "would a reader asking the
 * selection's question be surprised by the rows that are missing".**
 *
 * - `v_awaiting_final_cost` — `wo.cost_status = 'awaiting_final_cost'`. "How many
 *   work orders, by vendor" answered from here reports only those closed and
 *   still owed a cost.
 * - `v_work_order_queue` — unassigned, not closed, and older than two days. "Work
 *   orders by stage" answered from here silently means "unassigned and aged, by
 *   stage".
 *
 * Three views deliberately NOT here, because their predicate is what makes the
 * measure exist rather than a restriction on a population the reader had in mind:
 * `v_purchase_order_invoice_variance` (a variance needs a linked order — and it is
 * the sole carrier of `variance_amount` and `purchase_order`, so it can win
 * nothing else), `v_vendor_outstanding_balance` (a balance outstanding needs an
 * unpaid invoice; sole carrier of `outstanding_amount`), and
 * `v_work_order_category_spend` (a category row needs a category link — its
 * over-count is documented on the dimension, and it is the sole carrier of
 * `category`). A draft predicate is not a narrowing either: every view over
 * `work_order` carries one, so excluding drafts is the population, not a subset
 * of it.
 */
export const REPORT_VIEW_IS_NARROWED: ReadonlySet<ReportSourceView> = new Set([
  "v_awaiting_final_cost",
  "v_work_order_queue",
]);

/**
 * The views whose measures are BALANCES as of now, so no timeframe applies.
 * TRBUD-336, finding 30.
 *
 * Their run says "as of now" rather than the generic "covers all time": a
 * balance is not a total over every date, it is what stands today, and a reader
 * told "all time" could take on-hand stock for everything ever received.
 */
export const REPORT_VIEW_IS_BALANCE: ReadonlySet<ReportSourceView> = new Set([
  "v_part_inventory_position",
]);

/** Matches `report_definition_source_view_chk`. A name, never SQL. */
export function isReportSourceViewName(value: string): boolean {
  return SOURCE_VIEW_PATTERN.test(value);
}

/** Is this one of the views a report is actually allowed to read? */
export function isKnownReportSourceView(value: string): value is ReportSourceView {
  return (REPORT_SOURCE_VIEWS as readonly string[]).includes(value);
}

export type SemanticFieldKind = "dimension" | "metric";

export interface SemanticField {
  /** Stable id. What a saved definition stores; the label may be re-worded. */
  id: string;
  label: string;
  kind: SemanticFieldKind;
  /** Gold view -> the column on it that backs this field. */
  columns: Partial<Record<ReportSourceView, string>>;
  /**
   * Divide the aggregate by this before display. Only ever a constant from this
   * file — `v_labour_roi` and `v_departmental_spend` publish **minutes**, and a
   * metric called "Labor hours" that reported minutes would be a wrong number
   * with a right name. Never applied to `count`, which counts rows.
   */
  divisor?: number;
  /**
   * A divisor for ONE view, where that view publishes the measure in another
   * unit. TRBUD-336: `v_asset_downtime_by_day` publishes exact seconds beside
   * hours already rounded per day, and adding rounded days drifts by up to half
   * a hundredth per day. Summing the seconds and dividing once is what the
   * Reports downtime chart does, so the two agree. Takes precedence over
   * `divisor` on that view; never applied to `count`.
   */
  divisorByView?: Partial<Record<ReportSourceView, number>>;
  /** `count` only — the field is a row identity, not a measure. */
  countOnly?: boolean;
  /** Rendering / formatting hint for the client. */
  unit?: "currency" | "hours" | "count" | "quantity" | "date" | "text";
  /** Set when the field is wholly or partly unbacked. Rendered, never hidden. */
  unmappedNote?: string;
}

export const SEMANTIC_DIMENSIONS: readonly SemanticField[] = [
  {
    id: "asset",
    label: "Asset",
    kind: "dimension",
    unit: "text",
    columns: {
      v_asset_cost_history: "unit_number",
      v_asset_downtime_by_period: "unit_number",
      v_asset_downtime_by_day: "unit_number",
      v_asset_master: "unit_number",
      v_awaiting_final_cost: "unit_number",
      // Both added by migration `0222` (TRBUD-218). `v_departmental_spend` is the
      // view that owns the vendor-versus-in-house split the RFP's financial
      // scenario names, and it published `work_order_number` as its ONLY
      // dimension — so that split could be crossed with nothing. The category
      // view is the same story for the asset-history scenario.
      v_departmental_spend: "unit_number",
      v_work_order_category_spend: "unit_number",
      v_work_order_queue: "unit_number",
      v_work_order_search: "unit_number",
    },
  },
  {
    /**
     * BACKED ON FIVE METRIC-CARRYING VIEWS as of migration `0222` (TRBUD-218),
     * and pointed at the column that has a WRITER.
     *
     * This entry read `columns: { v_asset_master: "current_location_name" }` with
     * a note saying no cost, downtime or work-order view carried a location. Both
     * halves were problems and only one of them was the one being complained
     * about:
     *
     * - The gap was real. `v_asset_master` carries no metric, so `terminal` was
     *   the single largest source of the builder's cross-view refusals — RFP
     *   scenario 1 asks for availability "by asset, terminal and period" and the
     *   builder could answer two of the three. `0222` adds `terminal_name` to
     *   `v_asset_cost_history`, `v_asset_downtime_by_period`,
     *   `v_departmental_spend`, `v_work_order_queue` and
     *   `v_work_order_category_spend` — a LEFT JOIN in the VIEW, which is the
     *   only place this architecture allows a join to live.
     * - `current_location_name` was DEAD. `asset.current_location_id` has no
     *   writer of any kind: migration `0062`'s comment records all 302 rows NULL,
     *   and `02-grants.sql` excludes the column from the home-base projector's
     *   UPDATE deliberately, because live position is Samsara's (`asset_position`,
     *   TRBUD-82/55). So a terminal report through that column returned one
     *   unlabelled bucket however many views carried it.
     *
     * `domicile_location_id` is the column with the writer (`project-home-base.ts`,
     * TRBUD-171) and the right meaning: a terminal is the yard that OWNS the
     * asset, not where the truck is standing this minute. Those are two axes and
     * coalescing them would collapse them. `v_asset_master` therefore maps to its
     * existing `domicile_location_name` — same derivation, same value, a column
     * it has published since `0202`.
     *
     * Cold start: NULL everywhere until the projector has run, which renders as
     * one null-labelled row. It warns and allows; it never refuses.
     */
    id: "terminal",
    label: "Terminal",
    kind: "dimension",
    unit: "text",
    columns: {
      v_asset_cost_history: "terminal_name",
      v_asset_downtime_by_period: "terminal_name",
      v_asset_master: "domicile_location_name",
      v_departmental_spend: "terminal_name",
      v_work_order_category_spend: "terminal_name",
      v_work_order_queue: "terminal_name",
    },
    // Worded for the person reading a report, not for the developer who built it
    // (TRBUD-359): this text is shown as a tooltip, in the builder's preview, in a run's
    // caveat and in the exported CSV, so it names no view, column, migration or ticket.
    // Three fragments are pinned by tests (HOME BASE, NULL on every row until).
    unmappedNote:
      "Terminal is the asset's HOME BASE, not its live position (that comes from Samsara), " +
      "so spend, downtime and queue age can each be grouped by terminal. It is NULL on " +
      "every row until home bases have been assigned to assets, which reads as one " +
      "unlabelled row rather than an error.",
  },
  {
    id: "vendor",
    label: "Vendor",
    kind: "dimension",
    unit: "text",
    columns: {
      v_awaiting_final_cost: "vendor_name",
      // The vendor who SENT the invoice, by way of the order it was raised
      // against — `purchase_order.vendor_id`, not `cost_entry.vendor_id`. They
      // agree today (`add_cost_to_invoice` refuses a cost whose vendor differs
      // from the invoice's), and taking it off the order is the reading that
      // stays right if they ever stop agreeing: the question is "who billed us
      // differently from what we ordered".
      v_purchase_order_invoice_variance: "vendor_name",
      v_vendor_outstanding_balance: "vendor_name",
      v_vendor_spend_ranking: "vendor_name",
      /*
       * `v_departmental_spend` IS DELIBERATELY NOT HERE, though migration `0222`
       * gave it a `vendor_name` column. Two independent reasons, and the first is
       * the array-order trap this file warns about at the top:
       *
       * 1. It sorts at index 4 and `v_vendor_spend_ranking` at 10, so mapping it
       *    would MOVE `cost_total` by `vendor` off the ranking view — and change
       *    the number, because the ranking attributes each cost LINE to the
       *    vendor on it while this view would attribute a whole work order's
       *    total to its primary vendor. That is a wrong number with a right name,
       *    and the built-in escaping it through `preferredView` would hide the
       *    breakage from the one report most likely to be checked.
       * 2. The measures this view exists for do not take the dimension. Grouping
       *    `internal_labour_amount` by vendor asks who supplied our own
       *    mechanics' hours, which has no answer.
       *
       * The vendor-versus-in-house question the RFP names is answered on this
       * view by putting the two metrics in the metric and compare wells and
       * grouping by ASSET, TERMINAL or work order — all three of which `0222`
       * makes possible. The column is still published because the view is read
       * directly as well as through the builder.
       */
      // NULL on a queue row with no primary vendor, which is most of them.
      // `primary_vendor_id` is billing rather than assignment — see the view's
      // COMMENT — so grouping the unassigned-and-aged queue by vendor reads as
      // "who is on the hook to bill", not "who owns the job".
      v_work_order_queue: "vendor_name",
      v_work_order_search: "vendor_name",
    },
  },
  {
    id: "category",
    label: "Category",
    kind: "dimension",
    unit: "text",
    /**
     * BACKED as of migration 0172 (TRBUD-154). This entry read `columns: {}`
     * with a note saying no gold view exposed `work_order_category_link` at all;
     * `v_work_order_category_spend` is that view.
     *
     * The NAME, never the uuid. `read/insights.ts` renders the dimension column
     * verbatim as the row label, so a mapped `work_order_category_id` would put
     * a raw uuid on the axis of every chart grouped by category.
     */
    columns: { v_work_order_category_spend: "category_name" },
    // Worded for the person reading a report (TRBUD-359); see the terminal note. Two
    // fragments are pinned by tests (counted under BOTH, VENDOR and MECHANIC are still
    // not crossable).
    unmappedNote:
      "A work order with several categories is counted under BOTH, so this column must not " +
      "be added up across categories; the footer on the Reports screen counts each work " +
      "order once. A category can be crossed with asset and terminal only. VENDOR and " +
      "MECHANIC are still not crossable with it, because a work order can carry several of " +
      "each and its cost would be counted a second time.",
  },
  {
    /**
     * The purchase order a variance is measured against.
     *
     * The NUMBER, not the uuid, for the reason `category` gives: the dimension
     * column is rendered as the row label. `purchase_order.number` is a
     * `GENERATED ALWAYS AS IDENTITY` bigint, and `runReportDefinition` casts
     * every label to text, so it reads as the number a person quotes on a
     * phone call.
     */
    id: "purchase_order",
    label: "Purchase order",
    kind: "dimension",
    unit: "text",
    columns: { v_purchase_order_invoice_variance: "purchase_order_number" },
  },
  {
    id: "stage",
    label: "Stage",
    kind: "dimension",
    unit: "text",
    columns: { v_work_order_queue: "stage_name", v_work_order_search: "stage_name" },
  },
  {
    id: "priority",
    label: "Priority",
    kind: "dimension",
    unit: "text",
    columns: { v_work_order_queue: "priority", v_work_order_search: "priority" },
  },
  {
    id: "work_order_type",
    label: "Work-order type",
    kind: "dimension",
    unit: "text",
    columns: {
      v_work_order_cohort: "work_order_type_code",
      // The queue view publishes both `work_order_type_code` and
      // `work_order_type_name`; the NAME is mapped, matching v_work_order_search,
      // so the same dimension does not read as a code on one view and a label on
      // another.
      v_work_order_queue: "work_order_type_name",
      v_work_order_search: "work_order_type_name",
    },
  },
  {
    id: "created_date",
    label: "Created date",
    kind: "dimension",
    unit: "date",
    columns: {
      v_work_order_cohort: "cohort_month",
      v_work_order_queue: "created_at",
      v_work_order_search: "created_at",
    },
  },
  {
    id: "completed_date",
    label: "Completed date",
    kind: "dimension",
    unit: "date",
    columns: { v_awaiting_final_cost: "completed_at", v_work_order_search: "completed_at" },
  },
  {
    id: "period",
    label: "Period",
    kind: "dimension",
    unit: "date",
    columns: {
      v_asset_cost_history: "period_start",
      v_asset_downtime_by_period: "period_start",
      v_labour_roi: "period_start",
      v_vendor_spend_ranking: "period_start",
    },
  },
  {
    id: "cost_kind",
    label: "Cost kind",
    kind: "dimension",
    unit: "text",
    columns: { v_asset_cost_history: "attributed_cost_kind" },
  },
  {
    id: "mechanic",
    label: "Mechanic",
    kind: "dimension",
    unit: "text",
    columns: { v_labour_roi: "display_name" },
  },
  {
    id: "part",
    label: "Part",
    kind: "dimension",
    unit: "text",
    columns: {
      v_part_inventory_position: "part_number",
      // NULL on a variance row for labour, tax or shipping, and on a line the
      // entry-time matcher paired with the order by DESCRIPTION rather than by
      // part — `cost_entry` carries `part_id` and no description, so the ledger
      // genuinely does not record which line that was. Grouping by part there
      // reads as an unlabelled bucket, which is the honest rendering of "we
      // cannot say".
      v_purchase_order_invoice_variance: "part_number",
    },
  },
  {
    id: "work_order",
    label: "Work order",
    kind: "dimension",
    unit: "text",
    columns: {
      v_awaiting_final_cost: "work_order_number",
      v_departmental_spend: "work_order_number",
      v_purchase_order_invoice_variance: "work_order_number",
      v_work_order_category_spend: "work_order_number",
      v_work_order_cost_total: "work_order_number",
      v_work_order_queue: "work_order_number",
      v_work_order_search: "work_order_number",
    },
  },
];

export const SEMANTIC_METRICS: readonly SemanticField[] = [
  {
    id: "cost_total",
    label: "Cost — total",
    kind: "metric",
    unit: "currency",
    columns: {
      v_asset_cost_history: "amount",
      v_departmental_spend: "total_amount",
      v_vendor_spend_ranking: "amount",
      // Read from v_work_order_cost_total by the view itself, never recomputed
      // from cost_entry — so a category total and a work-order total cannot
      // disagree with the ledger or with each other.
      v_work_order_category_spend: "cost_total",
      v_work_order_cost_total: "total_amount",
    },
  },
  {
    id: "cost_vendor",
    label: "Cost — vendor",
    kind: "metric",
    unit: "currency",
    columns: { v_departmental_spend: "vendor_labour_amount" },
    // Worded for the person reading a report (TRBUD-359); see the terminal note.
    unmappedNote:
      "Vendor-side spend is vendor labor. Purchased parts are a separate figure, " +
      "Cost — purchased parts, and are not included in it.",
  },
  {
    id: "cost_in_house",
    label: "Cost — in-house",
    kind: "metric",
    unit: "currency",
    columns: { v_departmental_spend: "internal_labour_amount" },
  },
  {
    id: "cost_purchased_parts",
    label: "Cost — purchased parts",
    kind: "metric",
    unit: "currency",
    columns: { v_departmental_spend: "purchased_part_amount" },
  },
  {
    id: "downtime_hours",
    /*
     * TRBUD-222 / DEC-03. This measure is the GROSS number on BOTH its source
     * views and the label now says so — a report author picking "Downtime hours"
     * gets total time off the road, stoppages included.
     *
     * The `id` is deliberately unchanged: it is stored in
     * `report_definition.selection`, so renaming it would silently break every
     * saved report. Only the human-facing label moves.
     */
    label: `${ASSET_DOWNTIME_LABEL} hours`,
    kind: "metric",
    unit: "hours",
    columns: {
      v_asset_downtime_by_period: "downtime_hours",
      // Exact SECONDS, divided once (`divisorByView` below), as the Reports
      // downtime chart sums them. The view's own `downtime_hours` is rounded
      // per day, and adding rounded days drifts. TRBUD-336.
      v_asset_downtime_by_day: "downtime_seconds",
      // Read from v_work_order_downtime_split's GROSS `downtime_hours` column by
      // the view — never its `active_hours` — and not re-derived from
      // asset_downtime. Note the two are NOT interchangeable: the period view is
      // per ASSET per calendar month, this is per WORK ORDER, and only downtime
      // that names a work order reaches it.
      v_work_order_category_spend: "downtime_hours",
    },
    divisorByView: { v_asset_downtime_by_day: 3600 },
  },
  {
    /**
     * The COUNT of work orders in a group, expressible as a SUM.
     *
     * It exists because of a hard edge in this file: `resolveReportPlan` hands
     * the compare well the SAME aggregate as the metric well, and
     * `resolveMetric` refuses a `countOnly` metric under anything but `count`.
     * So `work_orders` (count) and `cost_total` (sum) cannot appear in one
     * report definition in either order — count beside sum throws, and sum
     * beside count silently becomes `count(DISTINCT cost_total)`, which is a
     * wrong number with a right name.
     *
     * Eden's ruling for TRBUD-154 asks for exactly that pairing: *"make sure
     * that the results of 'how many WOs were labeled as Mechanical' include the
     * cost of those WOs."* `v_work_order_category_spend` is one row per
     * (category, work order), so the backing column is literally `1` and summing
     * it counts the work orders in the group exactly — which lets one definition
     * answer both halves.
     *
     * The label says "once per category" because that is what the number means
     * and hiding it would be the `136.9%` defect wearing a nicer name: summed
     * across categories it is the number of LABELLINGS, not of work orders. Use
     * `work_orders` (countOnly, `count(DISTINCT work_order_id)`) when the
     * de-duplicated count is wanted, and read the fleet total off
     * `v_work_order_category_spend_total`.
     */
    id: "work_orders_labelled",
    label: "Work orders labelled (once per category)",
    kind: "metric",
    unit: "count",
    columns: { v_work_order_category_spend: "work_order_labelled" },
  },
  {
    /**
     * Invoiced minus ordered, in money, for a line whose part matched the order.
     *
     * Carried by ONE view and computed IN it, for the reason
     * `completion_percentage` states two entries down: a rate or a difference
     * derived anywhere but the view is how two surfaces start disagreeing. NULL
     * on a row where no order line matched — see the view's COMMENT — so a `sum`
     * skips it rather than counting the whole invoiced amount as variance.
     */
    id: "variance_amount",
    label: "Variance vs order",
    kind: "metric",
    unit: "currency",
    columns: { v_purchase_order_invoice_variance: "variance_amount" },
    // Worded for the person reading a report (TRBUD-359); see the terminal note. One
    // fragment is pinned by a test (matched a line on the linked purchase order).
    unmappedNote:
      "Only lines whose PART matched a line on the linked purchase order carry a number here. " +
      "A line matched by description alone, or not on the order at all, has a reason and no " +
      "amount, because the ledger cannot say which order line it was. Summing therefore " +
      "under-states the total difference, and the row count beside it is the honest measure " +
      "of how much went unmatched.",
  },
  {
    id: "labour_hours",
    label: "Labor hours",
    kind: "metric",
    unit: "hours",
    // v_labour_roi and v_departmental_spend both publish MINUTES.
    divisor: 60,
    columns: { v_departmental_spend: "repair_minutes", v_labour_roi: "repair_minutes" },
  },
  {
    id: "admin_hours",
    label: "Admin hours",
    kind: "metric",
    unit: "hours",
    divisor: 60,
    columns: { v_departmental_spend: "admin_minutes", v_labour_roi: "admin_minutes" },
  },
  {
    id: "labour_value",
    label: "Labor value posted",
    kind: "metric",
    unit: "currency",
    columns: { v_labour_roi: "posted_labour_value" },
  },
  {
    id: "parts_issued_quantity",
    label: "Parts qty issued",
    kind: "metric",
    unit: "quantity",
    columns: { v_departmental_spend: "inventory_issued_quantity" },
  },
  {
    id: "parts_on_hand",
    // "as of now" in the label itself, because the column header is what a
    // reader quotes. A balance follows no timeframe (TRBUD-336, finding 30).
    label: "Parts on hand (as of now)",
    kind: "metric",
    unit: "quantity",
    columns: { v_part_inventory_position: "on_hand_quantity" },
  },
  {
    id: "work_orders",
    label: "Work orders",
    kind: "metric",
    unit: "count",
    countOnly: true,
    columns: {
      v_awaiting_final_cost: "work_order_id",
      v_departmental_spend: "work_order_id",
      // `count(DISTINCT work_order_id)`, so a work order carrying two categories
      // counts ONCE within a category and once in a fleet-wide count — the
      // de-duplicated reading of the same view `work_orders_labelled` reads the
      // other way.
      v_work_order_category_spend: "work_order_id",
      v_work_order_cost_total: "work_order_id",
      v_work_order_queue: "work_order_id",
      v_work_order_search: "work_order_id",
    },
  },
  {
    // Queue-only, and therefore unambiguous: nothing else in the gold tier
    // publishes how long a work order has sat unassigned, so a selection naming
    // either of these can resolve to exactly one view.
    id: "queued_days",
    label: "Days waiting",
    kind: "metric",
    unit: "count",
    columns: { v_work_order_queue: "queued_days" },
  },
  {
    id: "queued_hours",
    label: "Hours waiting",
    kind: "metric",
    unit: "hours",
    // NO divisor. Unlike `labour_hours`, whose views publish minutes, the view
    // publishes hours already — and rounded to 2 in SQL, because `toCents`
    // THROWS on a third decimal place rather than mis-rendering one.
    columns: { v_work_order_queue: "queued_hours" },
  },
  {
    id: "outstanding_amount",
    label: "Vendor balance outstanding",
    kind: "metric",
    unit: "currency",
    columns: { v_vendor_outstanding_balance: "outstanding_amount" },
  },
  {
    id: "work_orders_created",
    label: "Work orders created",
    kind: "metric",
    unit: "count",
    columns: { v_work_order_cohort: "created_count" },
  },
  {
    id: "work_orders_completed",
    label: "Work orders completed",
    kind: "metric",
    unit: "count",
    columns: { v_work_order_cohort: "completed_count" },
  },
  {
    id: "completion_percentage",
    label: "Completion rate",
    kind: "metric",
    unit: "count",
    columns: { v_work_order_cohort: "completion_percentage" },
    // TRBUD-336, finding 28: this advice said "group by created date to read one
    // cohort per row", which is wrong. The view holds one row per month AND
    // work-order type, so a created-date group still mixes every type's rate.
    unmappedNote:
      "Completion rate is calculated separately for each creation month and work-order type. " +
      "Rates cannot be added or averaged into a fleet rate. A month holds one rate " +
      "per type: group by created date and split by work-order type to read one cohort per " +
      "row. For a month's rate across every type, report Work orders created and Work orders " +
      "completed by created date, or read the Reports completion card.",
  },
  {
    id: "labour_value_per_hour",
    label: "Labor value per repair hour",
    kind: "metric",
    unit: "currency",
    columns: { v_labour_roi: "posted_value_per_repair_hour" },
    unmappedNote:
      "Repair value divided by repair hours only; admin value is separate. Configured rates " +
      "do not measure payroll cost or savings. Legacy hand-reversed valuations retain recorded " +
      "hours and net money with an unresolved rate; void erroneous time entries to remove them. " +
      "Aggregating per-hour values is an average of averages; group by mechanic and period.",
  },
];

export const SEMANTIC_FIELDS: readonly SemanticField[] = [
  ...SEMANTIC_DIMENSIONS,
  ...SEMANTIC_METRICS,
];

export function semanticFieldById(id: string): SemanticField | null {
  return SEMANTIC_FIELDS.find((field) => field.id === id) ?? null;
}

/**
 * The list shape the Reports payload has always carried
 * (`ReportsResponse.semanticLayer`).
 *
 * Kept exactly as it was — two arrays of labels — because `wire.ts` types it that
 * way and the builder renders it. The richer field list travels on the reports
 * endpoints instead, so nothing on the existing screen has to change to keep
 * working.
 */
export const SEMANTIC_LAYER: { dimensions: string[]; metrics: string[] } = {
  dimensions: SEMANTIC_DIMENSIONS.map((field) => field.label),
  metrics: SEMANTIC_METRICS.map((field) => field.label),
};

/* ── the saved selection ─────────────────────────────────────────── */

export const REPORT_AGGREGATES = ["sum", "avg", "count", "min", "max"] as const;
export type ReportAggregate = (typeof REPORT_AGGREGATES)[number];

export const REPORT_CHART_TYPES = ["bar", "line", "stacked", "table", "kpi"] as const;
export type ReportChartType = (typeof REPORT_CHART_TYPES)[number];

export const REPORT_SORTS = ["value_desc", "value_asc", "label_asc"] as const;
export type ReportSort = (typeof REPORT_SORTS)[number];

export const REPORT_TIMEFRAMES = ["30d", "quarter", "ytd", "all"] as const;
export type ReportTimeframe = (typeof REPORT_TIMEFRAMES)[number];

/** A timeframe in words, as the builder's picker shows it, for a run's caveats. */
export const REPORT_TIMEFRAME_LABEL: Record<ReportTimeframe, string> = {
  "30d": "Last 30 days",
  quarter: "This quarter",
  ytd: "Year to date",
  all: "All time",
};

/**
 * What `report_definition.parameters` holds. **Semantic ids, never columns and
 * never predicates** — see the module comment.
 */
export interface ReportSelection {
  metric: string;
  /**
   * An optional SECOND measure, on the same view.
   *
   * Exists because two of the questions erd.md 15.1 reserves are comparisons and
   * neither is expressible as one column: *created versus completed* is
   * `created_count` beside `completed_count`, and *vendor versus in-house* is
   * `vendor_labour_amount` beside `internal_labour_amount`. Without it, either
   * question would have to be answered by two reports the reader compares by eye
   * — or by a client that subtracts one from the other, which is a caller
   * computing its own number.
   */
  compareMetric: string | null;
  aggregate: ReportAggregate;
  dimension: string | null;
  splitBy: string | null;
  /**
   * Narrow the report to one value of one dimension — "just this terminal".
   *
   * **Equality only, and deliberately.** The control this backs was disabled
   * because a filter editor "needs per-dimension operators and value domains,
   * and a place to store them". That is true of a general filter builder and
   * this is not one: one dimension, one value, `=`. No operator vocabulary, no
   * ranges, no AND/OR.
   *
   * The dimension is resolved through the SAME semantic layer as `dimension`
   * and `splitBy`, so a filter can only name a field a gold view already
   * carries, and the source view must carry it like any other selected field. A
   * saved report stays "a gold view name plus a selection, never SQL".
   *
   * The VALUE is a plain string compared against the column cast to text, which
   * is what keeps this from needing a type system per dimension. It is passed as
   * a parameter, never interpolated.
   *
   * **OPTIONAL, not nullable-and-required.** This shape is what
   * `report_definition.parameters` holds, and every row stored before this
   * existed has no `filter` key at all. A required field would make each of them
   * unreadable — `selectionFromStored` would reject a definition that was
   * perfectly valid when it was saved.
   */
  filter?: { dimension: string; value: string } | null;
  sort: ReportSort;
  timeframe: ReportTimeframe;
  chartType: ReportChartType;
  limit: number;
}

/** The largest row count a report may return, so no report can be a table scan dump. */
export const REPORT_ROW_LIMIT = 1_000;

export interface ReportDefinitionRow {
  reportDefinitionId: string;
  slug: string;
  name: string;
  sourceView: string;
  isBuiltin: boolean;
  ownedByMe: boolean;
  ownerDisplayName: string | null;
  selection: ReportSelection | null;
  columnSelection: string[];
  defaultGrouping: string | null;
  createdAt: string;
}

export interface ReportDefinitionsResponse {
  definitions: ReportDefinitionRow[];
  /** The governed vocabulary, with its backing stated field by field. */
  fields: SemanticField[];
  /**
   * Built-in slugs erd.md 15.1 names that could NOT be seeded because the view
   * they cite does not exist. Reported rather than silently dropped.
   */
  unavailableBuiltins: Array<{ slug: string; sourceView: string; reason: string }>;
}

export interface ReportColumn {
  key: string;
  label: string;
  unit: SemanticField["unit"];
  /** True for the aggregated measure column. */
  isMeasure: boolean;
}

export interface ReportRunResponse {
  reportDefinitionId: string;
  name: string;
  sourceView: string;
  selection: ReportSelection;
  columns: ReportColumn[];
  /** Values are strings on the wire — a decimal never becomes a double here. */
  rows: Array<Record<string, string | null>>;
  rowCount: number;
  truncated: boolean;
  /** What the number rests on, in the same voice the KPI cards use. */
  caveat: string | null;
}

/* ── request bodies ──────────────────────────────────────────────── */

/**
 * The builder's equality filter as it travels on a save: one approved dimension
 * id and one value.
 *
 * **This checks SHAPE and nothing else.** Whether `dimension` is an approved
 * dimension, and whether a single gold view carries it beside the report's other
 * fields, is `resolveReportPlan`'s job — the same function the builder's preview
 * runs and the save route runs before it writes, so a filter the preview refuses
 * is refused here in the same words (422). There is deliberately no second
 * validator: a copy of that rule is a second place for the preview and the save
 * to disagree, and a disagreement here is a saved report that quietly means
 * something other than what was built.
 *
 * **A blank value is refused (400), not read as "no filter".** The builder never
 * sends one — it sends `null` when either half is empty, so a dimension with no
 * value is not a filter. Any other caller that sends a dimension and an empty
 * value has made a mistake, and running its report unfiltered would answer a
 * different question from the one asked without saying so.
 *
 * Strict like every request body (UAT 0908 finding 13): a misspelled key inside
 * the filter would otherwise be dropped and the report saved unfiltered.
 */
export const reportFilterBody = strictObject({
  dimension: z.string().trim().min(1).max(64),
  // 200 is the builder's own `maxLength` on the value box.
  value: z.string().trim().min(1).max(200),
});

/**
 * The builder's Save payload.
 *
 * Note what is **absent**: no `sourceView`, no column names, no filter
 * *expression*. The caller names approved fields — the filter included, as a
 * dimension id and a value — and the server resolves the view. A body that could
 * name its own view would move the trust boundary to whoever remembered to
 * validate it next.
 *
 * `filter` is optional because a report without one is the ordinary case, and
 * because a client from before it existed sends no such key. It is what the
 * builder's "Filter — field / equals" boxes become when a report is saved; until
 * TRBUD-330 this body had no such key, so the box shaped the preview and was
 * dropped on Save, and every saved report ran unfiltered.
 */
export const saveReportBody = strictObject({
  name: z.string().trim().min(1).max(200),
  metric: z.string().trim().min(1).max(64),
  compareMetric: z.string().trim().max(64).nullish(),
  aggregate: z.enum(REPORT_AGGREGATES).default("sum"),
  dimension: z.string().trim().max(64).nullish(),
  splitBy: z.string().trim().max(64).nullish(),
  filter: reportFilterBody.nullish(),
  sort: z.enum(REPORT_SORTS).default("value_desc"),
  timeframe: z.enum(REPORT_TIMEFRAMES).default("30d"),
  chartType: z.enum(REPORT_CHART_TYPES).default("bar"),
  limit: z.number().int().positive().max(REPORT_ROW_LIMIT).default(100),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type SaveReportBody = z.infer<typeof saveReportBody>;

/**
 * Overrides a run may apply on top of a saved definition. Still semantic-only.
 *
 * `.strict()` for the reason F-37 gives on `queueQuery` (TRBUD-152): a zod object
 * strips unknown keys, so an override the caller misspelled would be DISCARDED
 * and the run would answer 200 with the saved definition's own timeframe and
 * limit. A report is a number somebody acts on, so silently running a different
 * report from the one asked for is the worst version of this defect — nothing in
 * the response says the override did not apply.
 */
export const runReportQuery = z
  .object({
    timeframe: z.enum(REPORT_TIMEFRAMES).optional(),
    limit: z.coerce.number().int().positive().max(REPORT_ROW_LIMIT).optional(),
  })
  .strict();
export type RunReportQuery = z.infer<typeof runReportQuery>;

export const exportReportBody = strictObject({
  format: z.literal("csv").default("csv"),
  timeframe: z.enum(REPORT_TIMEFRAMES).optional(),
  limit: z.number().int().positive().max(REPORT_ROW_LIMIT).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type ExportReportBody = z.infer<typeof exportReportBody>;

/**
 * Turn a validated body into the selection a definition stores.
 *
 * Every selection field the body can carry is carried. `filter` was the one that
 * was not (TRBUD-330): it is optional on `ReportSelection`, so leaving it out of
 * the object below COMPILED, the builder sent nothing, and the stored selection
 * had no filter however the report was built. A field added to `SaveReportBody`
 * belongs in this `Pick` and in that object, and `reports.test.ts` walks the
 * body's keys to say so if one is missed.
 */
export function selectionFromBody(
  body: Pick<
    SaveReportBody,
    | "metric"
    | "compareMetric"
    | "aggregate"
    | "dimension"
    | "splitBy"
    | "filter"
    | "sort"
    | "timeframe"
    | "chartType"
    | "limit"
  >,
): ReportSelection {
  return {
    metric: body.metric,
    compareMetric: body.compareMetric ?? null,
    aggregate: body.aggregate,
    dimension: body.dimension ?? null,
    splitBy: body.splitBy ?? null,
    filter: body.filter ?? null,
    sort: body.sort,
    timeframe: body.timeframe,
    chartType: body.chartType,
    limit: body.limit,
  };
}

/**
 * A selection as a plain JSON record, for `sql.json(...)`.
 *
 * Written out field by field rather than spread, and that is not ceremony:
 * postgres.js accepts `JSONValue`, an interface has no implicit index signature,
 * and the usual escapes (`as never`, `JSON.stringify`) are how a delta ends up
 * double-encoded as a jsonb **string** — the defect
 * `apps/api/src/jsonb-parameters.test.ts` exists to pin. An explicit record also
 * means a field added to `ReportSelection` fails to compile here until someone
 * decides whether it belongs in the stored parameters.
 */
export function selectionToJson(
  selection: ReportSelection,
): Record<string, string | number | null | { dimension: string; value: string }> {
  return {
    metric: selection.metric,
    compareMetric: selection.compareMetric,
    aggregate: selection.aggregate,
    dimension: selection.dimension,
    splitBy: selection.splitBy,
    // Normalised to null rather than omitted, so the stored shape has the same
    // keys whether or not a filter was chosen. `selectionFromStored` reads an
    // absent key and an explicit null identically, so old rows are unaffected.
    filter: selection.filter ?? null,
    sort: selection.sort,
    timeframe: selection.timeframe,
    chartType: selection.chartType,
    limit: selection.limit,
  };
}

/**
 * Read a stored `parameters` blob back as a selection, or null.
 *
 * Tolerant on purpose: a definition written by an older build must not make the
 * whole list 500. An unreadable selection renders as a definition that cannot be
 * run, which is visible and recoverable.
 */
export function selectionFromStored(value: unknown): ReportSelection | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const metric = typeof raw.metric === "string" ? raw.metric : null;
  if (metric === null) return null;
  const oneOf = <T extends string>(list: readonly T[], candidate: unknown, fallback: T): T =>
    typeof candidate === "string" && (list as readonly string[]).includes(candidate)
      ? (candidate as T)
      : fallback;
  const limit = typeof raw.limit === "number" && Number.isInteger(raw.limit) ? raw.limit : 100;
  return {
    metric,
    compareMetric:
      typeof raw.compareMetric === "string" && raw.compareMetric !== "" ? raw.compareMetric : null,
    aggregate: oneOf(REPORT_AGGREGATES, raw.aggregate, "sum"),
    dimension: typeof raw.dimension === "string" && raw.dimension !== "" ? raw.dimension : null,
    splitBy: typeof raw.splitBy === "string" && raw.splitBy !== "" ? raw.splitBy : null,
    // A stored filter is read defensively: both halves must be non-empty strings
    // or the whole thing is dropped. A half-formed filter is not narrowed to
    // "everything" — it is discarded, so a definition saved by an older or
    // broken client runs UNFILTERED and visibly returns too many rows, rather
    // than matching the empty string and returning none.
    filter:
      typeof raw.filter === "object" && raw.filter !== null && !Array.isArray(raw.filter)
        ? (() => {
            const f = raw.filter as Record<string, unknown>;
            return typeof f.dimension === "string" &&
              f.dimension !== "" &&
              typeof f.value === "string" &&
              f.value.trim() !== ""
              ? { dimension: f.dimension, value: f.value }
              : null;
          })()
        : null,
    sort: oneOf(REPORT_SORTS, raw.sort, "value_desc"),
    timeframe: oneOf(REPORT_TIMEFRAMES, raw.timeframe, "30d"),
    chartType: oneOf(REPORT_CHART_TYPES, raw.chartType, "bar"),
    limit: Math.min(Math.max(limit, 1), REPORT_ROW_LIMIT),
  };
}

/* ── resolution: a selection becomes a view plus columns ─────────── */

export interface ResolvedReportPlan {
  sourceView: ReportSourceView;
  metric: SemanticField;
  metricColumn: string;
  compareMetric: SemanticField | null;
  compareColumn: string | null;
  compareDivisor: number;
  dimension: SemanticField | null;
  dimensionColumn: string | null;
  splitBy: SemanticField | null;
  splitColumn: string | null;
  /** The equality filter, resolved to a column. Null when none is selected. */
  filter: { field: SemanticField; column: string; value: string } | null;
  aggregate: ReportAggregate;
  divisor: number;
  /** The column the timeframe filters on, or null when the view has no period. */
  periodColumn: string | null;
  /**
   * True when `periodColumn` is a calendar-month bucket, so the window start
   * must be floored to the 1st before it is applied. See
   * `REPORT_PERIOD_IS_MONTH_BUCKET` — a day-granular threshold against month
   * buckets discards a whole month, which is TRBUD-85.
   */
  periodIsMonthBucket: boolean;
  /**
   * True when `periodColumn` is a `timestamptz`, so the window starts at
   * fleet-zone midnight rather than at the session's. See
   * `REPORT_PERIOD_IS_INSTANT` (TRBUD-336).
   */
  periodIsInstant: boolean;
  /** Gold-view columns, in display order. Exactly what may be selected. */
  columnSelection: string[];
  /** Everything the run has to admit about the number. Rendered, never hidden. */
  caveats: string[];
}

export class ReportResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReportResolutionError";
  }
}

/**
 * Pick the one gold view that carries every field the selection names.
 *
 * The metric decides the candidate set; the dimension and the split narrow it.
 * When nothing carries all three the refusal **names which pairing failed**,
 * because "no view supports this" sends the reader looking for a bug rather than
 * for the missing column. Deterministic: candidates are considered in the order
 * `REPORT_SOURCE_VIEWS` declares, so the same selection always resolves the same
 * way and a saved report cannot drift onto a different view between runs.
 *
 * ## `preferredView` — a stored view wins when it CAN answer the selection
 *
 * A selection cannot express everything a view means. `v_awaiting_final_cost` is
 * *closed and still owed a cost*; `v_work_order_queue` is *unassigned and older
 * than two days*. Both are reachable as "count work orders", and the filter that
 * makes each of them the answer to its question lives **in the view**, because a
 * report is a view name plus a selection and has nowhere for a `WHERE` to live.
 * Ask those two built-ins to round-trip through a selection alone and the meaning
 * is gone — `awaiting_final_cost` is `work_orders` by `vendor`, which several
 * views carry, and `unassigned_aged` is `work_orders` by `stage`, which two do.
 *
 * So when a caller already HAS a view — a stored definition being run, never the
 * builder — that name is honoured, provided it is a member of `supported`. That
 * proviso is the whole safety property and it is unchanged:
 *
 * **A stored view that is NOT among `supported` still refuses.** That is the
 * honest case `assertSourceView`'s "does not claim" check exists for — a
 * selection the stored view genuinely cannot answer, because some field it names
 * has no column there. Running it anyway would report a number from a view that
 * cannot produce it. What this parameter removes is only the *other* case: the
 * refusal of a stored view that carries every selected field and lost on array
 * position to an alphabetically earlier one.
 *
 * With no `preferredView`, `supported[0]` is taken exactly as before, so the
 * builder path — a NEW report, no stored view — is untouched.
 */
export function resolveReportPlan(
  selection: ReportSelection,
  preferredView?: ReportSourceView,
): ResolvedReportPlan {
  const metric = resolveMetric(selection.metric, selection.aggregate, "Value");
  const compareMetric =
    selection.compareMetric === null || selection.compareMetric === ""
      ? null
      : resolveMetric(selection.compareMetric, selection.aggregate, "Compare");

  const dimension = resolveDimension(selection.dimension, "X axis");
  const splitBy = resolveDimension(selection.splitBy, "Split by");
  // Resolved through the same registry as the other two, so a filter cannot name
  // a field no gold view carries — and an empty value is treated as "no filter"
  // rather than as "equals empty string", which would silently return nothing.
  const filterField =
    !selection.filter || selection.filter.value.trim() === ""
      ? null
      : resolveDimension(selection.filter.dimension, "Filter");

  const supported = REPORT_SOURCE_VIEWS.filter((view) => {
    if (metric.columns[view] === undefined) return false;
    if (compareMetric && compareMetric.columns[view] === undefined) return false;
    if (dimension && dimension.columns[view] === undefined) return false;
    if (splitBy && splitBy.columns[view] === undefined) return false;
    if (filterField && filterField.columns[view] === undefined) return false;
    return true;
  });

  /*
   * The default pick prefers an UNNARROWED view over a narrowed one, falling back
   * to array position within each group. TRBUD-218.
   *
   * `supported` is already in `REPORT_SOURCE_VIEWS` order, so a stable partition
   * keeps the old tiebreak intact everywhere it was not actively wrong: among
   * unnarrowed candidates the first still wins, and among narrowed ones (which is
   * every candidate, for a selection only the queue view can answer) so does the
   * first. Nothing is excluded — `queued_days` lives on `v_work_order_queue`
   * alone and still resolves to it.
   */
  const ranked = [
    ...supported.filter((view) => !REPORT_VIEW_IS_NARROWED.has(view)),
    ...supported.filter((view) => REPORT_VIEW_IS_NARROWED.has(view)),
  ];

  // The stored view wins only from INSIDE `supported` — never as an override of
  // it. A view that does not carry every selected field is not a candidate here
  // any more than it was before. Checked against `supported` rather than `ranked`
  // deliberately: they hold the same members, and asking the unreordered list
  // keeps "is this view able to answer the selection" the only question the
  // proviso asks.
  const sourceView =
    preferredView !== undefined && supported.includes(preferredView)
      ? preferredView
      : ranked[0];
  if (sourceView === undefined) {
    const carriers = (field: SemanticField) =>
      Object.keys(field.columns).length === 0
        ? "no gold view at all"
        : Object.keys(field.columns).join(", ");
    const parts = [`"${metric.label}" is on ${carriers(metric)}`];
    if (compareMetric) parts.push(`"${compareMetric.label}" is on ${carriers(compareMetric)}`);
    if (dimension) parts.push(`"${dimension.label}" is on ${carriers(dimension)}`);
    if (splitBy) parts.push(`"${splitBy.label}" is on ${carriers(splitBy)}`);
    if (filterField) parts.push(`"${filterField.label}" is on ${carriers(filterField)}`);
    throw new ReportResolutionError(
      `No single gold view carries that combination, so the report cannot be built without ` +
        `joining across views — which is how two reports start disagreeing. ${parts.join("; ")}.`,
    );
  }

  const metricColumn = metric.columns[sourceView] as string;
  const compareColumn = compareMetric ? (compareMetric.columns[sourceView] as string) : null;
  const dimensionColumn = dimension ? (dimension.columns[sourceView] as string) : null;
  const splitColumn = splitBy ? (splitBy.columns[sourceView] as string) : null;
  const filterColumn = filterField ? (filterField.columns[sourceView] as string) : null;

  const columnSelection: string[] = [];
  const push = (column: string | null) => {
    if (column !== null && !columnSelection.includes(column)) columnSelection.push(column);
  };
  push(dimensionColumn);
  push(splitColumn);
  push(metricColumn);
  push(compareColumn);

  const periodColumn = REPORT_PERIOD_COLUMN[sourceView];
  const caveats: string[] = [];
  if ([metric, compareMetric].some((field) =>
    field?.id === "work_orders" || field?.id === "work_orders_labelled" ||
    field?.id === "work_orders_created" || field?.id === "work_orders_completed" ||
    field?.id === "completion_percentage")) {
    caveats.push("Work-order figures include MaintainX history and exclude drafts and visits.");
  }
  if (sourceView === "v_work_order_category_spend") {
    caveats.push("Category costs include MaintainX history. Downtime includes TruckBuddy intervals only; MaintainX downtime history is not included.");
  }
  if (periodColumn === null && selection.timeframe !== "all") {
    caveats.push(
      // A balance is what stands today, so "all time" would mislead (TRBUD-336).
      REPORT_VIEW_IS_BALANCE.has(sourceView)
        ? `${sourceView} holds balances as of now, so "${REPORT_TIMEFRAME_LABEL[selection.timeframe]}" ` +
            "does not apply: every part counts, whether or not it moved in that time."
        : `${sourceView} has no period column, so the ${selection.timeframe === "30d" ? "30-day" : selection.timeframe === "ytd" ? "year-to-date" : "quarter"} timeframe is NOT applied — ` +
            "this covers all time. Narrowing it needs a period on the view, not a filter here.",
    );
  }
  if (periodColumn !== null && REPORT_PERIOD_IS_MONTH_BUCKET[sourceView] && selection.timeframe === "30d") {
    // TRBUD-85: floored to the month rather than dropping one. The run names the
    // exact first day (`reportWindowCaveat` in read/insights.ts, TRBUD-336); the
    // builder's preview has no date, so this says how the window is chosen.
    caveats.push(
      `${sourceView} buckets by calendar month, so "Last 30 days" starts on the 1st of the ` +
        "month containing the first of the last 30 calendar days (including today), and counts whole months, including days before that window.",
    );
  }
  /*
   * When the report IS on a narrowed view, say so in words. TRBUD-218.
   *
   * The resolver now prefers an unnarrowed view, so this fires in the two honest
   * cases: the selection can only be answered here (`queued_days`), or a stored
   * definition asked for it (`awaiting_final_cost`, `unassigned_aged`). Both are
   * correct and both are still worth stating — the population is the one thing a
   * selection cannot express, so it is the one thing the reader cannot infer from
   * the chart. This is the same reasoning as the no-period caveat above: an
   * unstated restriction is how two surfaces come to disagree about one fact.
   */
  if (REPORT_VIEW_IS_NARROWED.has(sourceView)) {
    caveats.push(
      `${sourceView} does not hold every work order — it is restricted to ` +
        (sourceView === "v_awaiting_final_cost"
          // TRBUD-336, finding 13: this said "closed and still owed a final
          // cost", but the view tests only the flag, which can be set at any
          // stage, cancelled jobs included.
          ? "work orders flagged awaiting final cost, at any stage, finished or not"
          : "work orders that are unassigned, not closed and older than two days") +
        ". Read the figure as being about that group, not about the fleet.",
    );
  }
  /*
   * TRBUD-336, finding 24. The category view picks work orders by the day they
   * were opened, then counts everything they have cost and every hour they were
   * down to date. So "Year to date" here is "work opened this year, and all of
   * its cost so far", not "money posted this year", and the file used to say only
   * "Timeframe ytd". Stated in words, since the two questions read the same.
   */
  if (sourceView === "v_work_order_category_spend" && selection.timeframe !== "all") {
    caveats.push(
      `${sourceView} picks work orders by the day they were opened, then counts all their ` +
        "costs and downtime to date, whenever those were posted.",
    );
  }
  for (const field of [metric, compareMetric, dimension, splitBy, filterField]) {
    if (field?.unmappedNote) caveats.push(`${field.label}: ${field.unmappedNote}`);
  }
  if (dimension === null && splitBy === null) {
    caveats.push("No dimension is selected, so this is a single figure over the whole window.");
  }

  return {
    sourceView,
    metric,
    metricColumn,
    compareMetric,
    compareColumn,
    // count counts rows; scaling it would be a wrong number with a right name.
    compareDivisor:
      selection.aggregate === "count" || compareMetric === null
        ? 1
        : divisorOn(compareMetric, sourceView),
    dimension,
    dimensionColumn,
    splitBy,
    splitColumn,
    filter:
      filterField !== null && filterColumn !== null && !!selection.filter
        ? { field: filterField, column: filterColumn, value: selection.filter.value.trim() }
        : null,
    aggregate: selection.aggregate,
    divisor: selection.aggregate === "count" ? 1 : divisorOn(metric, sourceView),
    periodColumn,
    periodIsMonthBucket: periodColumn !== null && REPORT_PERIOD_IS_MONTH_BUCKET[sourceView],
    periodIsInstant: periodColumn !== null && REPORT_PERIOD_IS_INSTANT[sourceView],
    columnSelection,
    caveats,
  };
}

/** The divisor a metric takes on one view: its per-view one first (TRBUD-336). */
function divisorOn(metric: SemanticField, view: ReportSourceView): number {
  return metric.divisorByView?.[view] ?? metric.divisor ?? 1;
}

function resolveMetric(id: string, aggregate: ReportAggregate, well: string): SemanticField {
  const metric = semanticFieldById(id);
  if (!metric || metric.kind !== "metric") {
    throw new ReportResolutionError(
      `"${id}" is not an approved metric, so it cannot go in the ${well} well. ` +
        "A report may only read the governed layer.",
    );
  }
  if (Object.keys(metric.columns).length === 0) {
    throw new ReportResolutionError(
      `"${metric.label}" has no backing column in any gold view` +
        (metric.unmappedNote ? ` — ${metric.unmappedNote}` : "."),
    );
  }
  if (metric.countOnly && aggregate !== "count") {
    throw new ReportResolutionError(
      `"${metric.label}" counts rows, so it can only be aggregated with count, not ${aggregate}.`,
    );
  }
  if (["completion_percentage", "labour_value_per_hour"].includes(metric.id) && ["sum", "avg"].includes(aggregate)) {
    throw new ReportResolutionError(
      `"${metric.label}" is a ratio. ${aggregate} of ratios is not a fleet-wide rate. ` +
      (metric.id === "completion_percentage"
        ? "Use Work orders created and Work orders completed by created date, or the Reports completion card."
        : "Use Labor value posted and Labor hours, or the Reports Labor ROI card.") +
      " Min and max can compare individual source rates.",
    );
  }
  return metric;
}

function resolveDimension(id: string | null, well: string): SemanticField | null {
  if (id === null || id === "") return null;
  const field = semanticFieldById(id);
  if (!field || field.kind !== "dimension") {
    throw new ReportResolutionError(
      `"${id}" is not an approved dimension, so it cannot go in the ${well} well.`,
    );
  }
  if (Object.keys(field.columns).length === 0) {
    throw new ReportResolutionError(
      `"${field.label}" has no backing column in any gold view` +
        (field.unmappedNote ? ` — ${field.unmappedNote}` : "") +
        ` It cannot be used in the ${well} well until one exists.`,
    );
  }
  return field;
}

/* ── slugs ───────────────────────────────────────────────────────── */

/**
 * A name becomes a slug matching `report_definition_slug_chk`.
 *
 * Returns null for a name with nothing slug-shaped in it (an emoji, CJK, pure
 * punctuation) rather than inventing one — the caller then asks for a name it can
 * store instead of writing `report_1` and losing what the user typed.
 */
export function slugifyReportName(name: string): string | null {
  // Underscore trimming is index arithmetic, not `/^_+|_+$/` — that regex (and
  // `/_+$/`) backtracks polynomially on adversarial underscore runs (CodeQL
  // js/polynomial-redos). The collapse guarantees no such runs exist, but the
  // checker cannot see that invariant, and the loop is linear on any input.
  const collapsed = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");
  // Trim again after the cut: slicing can leave a trailing separator behind.
  const base = trimUnderscores(trimUnderscores(collapsed).slice(0, 48));
  if (base === "" || !/^[a-z]/.test(base)) return null;
  return base;
}

function trimUnderscores(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === "_") start += 1;
  while (end > start && value[end - 1] === "_") end -= 1;
  return value.slice(start, end);
}

/**
 * The next free slug, given the ones already taken.
 *
 * `report_definition.slug` is UNIQUE **globally** per erd.md 15.1, so two people
 * saving "Vendor spend" collide. Suffixing is the least surprising resolution:
 * the save succeeds and the name the user typed is preserved in `name`, which is
 * what the screen shows.
 */
export function nextReportSlug(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
  throw new ReportResolutionError(
    `There are already 999 reports named like "${base}". Pick a different name.`,
  );
}

/* ── CSV ─────────────────────────────────────────────────────────── */

/**
 * RFC 4180 CSV. Server-side on purpose — see the route.
 *
 * A field is quoted when it contains a comma, a quote, a newline, or leading or
 * trailing whitespace that a reader would otherwise silently eat. A leading `=`,
 * `+`, `-` or `@` is additionally prefixed with a tab inside the quotes: those
 * four make Excel treat the cell as a **formula**, and a report is a file people
 * open in Excel. `null` becomes empty, never the four letters `null`.
 */
export function toCsv(
  columns: readonly string[],
  rows: ReadonlyArray<readonly (string | null)[]>,
  provenance: readonly (readonly [string, string])[] = [],
): string {
  const lines: string[] = [];
  // The provenance block, above the header, as ordinary two-column CSV rows
  // followed by a blank line. Not a `#` comment: RFC 4180 has no comment syntax
  // and Excel would render the hash as data anyway, so a labelled pair is the
  // form a spreadsheet actually shows usefully.
  for (const [label, value] of provenance) lines.push([label, value].map(csvField).join(","));
  if (provenance.length > 0) lines.push("");
  lines.push(columns.map(csvField).join(","));
  for (const row of rows) lines.push(row.map(csvField).join(","));
  // CRLF, which is what RFC 4180 says and what Excel expects.
  return `${lines.join("\r\n")}\r\n`;
}

/**
 * What a saved or emailed CSV has to say about itself.
 *
 * The export used to be the column labels and the rows and nothing else, so a
 * file mailed to a vendor to dispute a bill recorded neither the window it
 * covered nor the timezone that bucketed it. That is not theoretical: the fleet
 * timezone is under active change (TRBUD-98, New_York -> Chicago), and a
 * boundary-straddling downtime interval books into a different month under each
 * zone — measured as a 4 h / 10 h split one way and 5 h / 9 h the other. The same
 * export taken before and after would be a different file with nothing in it to
 * say which.
 *
 * Everything here is already known at the point the file is written; it was
 * simply not written. The screen carries the equivalent caption already ("Top 12
 * units, last 30 days"), so this is the file catching up with the screen.
 */
export function reportProvenanceRows(input: {
  name: string;
  sourceView: string;
  timeframe: string;
  /**
   * The equality filter the figures are narrowed to, when the report has one.
   *
   * It says which rows the numbers are OVER, which is the one thing a column of
   * figures cannot show for itself: a file that totals one unit's downtime and a
   * file that totals the fleet's look identical without it. Optional, and absent
   * or null writes nothing, so every file made before a report could carry a
   * filter is byte-for-byte what it was (TRBUD-330).
   */
  filter?: { dimension: string; value: string } | null;
  timeZone: string;
  generatedAt: string;
  rowCount: number;
  truncated: boolean;
  caveat: string | null;
}): Array<readonly [string, string]> {
  // The field's label, as the column header prints it; the id itself if the field
  // has since been renamed out of the registry, so the row never goes blank.
  const filterRows: Array<readonly [string, string]> = input.filter
    ? [
        [
          "Filter",
          `${semanticFieldById(input.filter.dimension)?.label ?? input.filter.dimension} = ${input.filter.value}`,
        ],
      ]
    : [];
  const rows: Array<readonly [string, string]> = [
    ["Report", input.name],
    ["Source view", input.sourceView],
    ["Timeframe", input.timeframe],
    ...filterRows,
    ["Fleet time zone", input.timeZone],
    ["Generated at", input.generatedAt],
    ["Rows", input.truncated ? `${input.rowCount} (truncated)` : String(input.rowCount)],
  ];
  // The caveat travels with the file for the same reason it travels with the
  // run: a number whose qualification is left behind on the screen is a number
  // that will be quoted without it.
  if (input.caveat !== null) rows.push(["Caveat", input.caveat]);
  return rows;
}

export function csvField(value: string | null): string {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (/^[=+\-@]/.test(text) && !/^-?\d+(?:\.\d+)?$/.test(text)) text = `\t${text}`;
  if (/[",\r\n]/.test(text) || text !== text.trim()) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}
